import React, { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

export interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  category: string;
  publicUrl?: string;
  tags?: string[];
}

interface GalleryImageGridProps {
  category?: string;
  onImageSelect?: (image: GalleryImage) => void;
  useStorageBucket?: boolean;
  images?: GalleryImage[];
  onSelect?: (image: GalleryImage) => void;
  loading?: boolean;
  onCopyUrl?: (image: GalleryImage) => void;
}

// Regular expressions for identifying screenshots
const SCREENSHOT_PATTERNS = [
  /screenshot/i,
  /screen-?shot/i,
  /kuvakaappaus/i,
  /näyttökuva/i,
  /ruudunkaappaus/i,
  /-ss-/i,
  /ss_/i,
  /scr_/i,
  /_scr/i
];

// Check if a file is a screenshot based on its name
const isScreenshot = (fileName: string): boolean => {
  return SCREENSHOT_PATTERNS.some(pattern => pattern.test(fileName));
};

const GalleryImageGrid: React.FC<GalleryImageGridProps> = ({
  category,
  onImageSelect,
  useStorageBucket = true,
  images: providedImages,
  onSelect,
  loading: isLoading,
  onCopyUrl
}) => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if an image is from Lovable uploads
  const isLovableUpload = (src: string): boolean => {
    return src.includes('/lovable-uploads/');
  };

  // Use either provided images or fetch them from Supabase
  useEffect(() => {
    if (providedImages) {
      console.log(`Gallery received ${providedImages.length} images from parent`);
      setImages(providedImages);
      setLoading(false);
    } else if (category) {
      fetchImages(category);
    }
  }, [category, useStorageBucket, providedImages]);

  const fetchImages = async (category: string) => {
    setLoading(true);
    setError(null);
    
    try {
      let galleryImages: GalleryImage[] = [];
      
      if (useStorageBucket) {
        // Fetch images from Supabase by category
        if (category !== 'lovable') {
          const { data, error } = await supabase.storage
            .from('article-images')
            .list(category, {
              sortBy: { column: 'created_at', order: 'desc' }
            });
          
          if (error) {
            throw error;
          }
          
          if (data) {
            // Filter out screenshots
            const filteredData = data.filter(file => 
              !file.id.endsWith('/') && file.name !== '.folder' && !isScreenshot(file.name.toLowerCase())
            );
            
            galleryImages = filteredData.map((file) => {
              const { data } = supabase.storage
                .from('article-images')
                .getPublicUrl(`${category}/${file.name}`);
                
              return {
                id: file.id,
                src: data.publicUrl,
                alt: file.name,
                category,
                publicUrl: data.publicUrl,
                tags: [category, file.name.split('.')[0]]
              };
            });
          }
        }
        
        // If category is "all" or "lovable", also try to fetch Lovable uploads
        if (category === "all" || category === "lovable") {
          try {
            const lovableImagesResponse = await fetch('/api/lovable-uploads.json');
            if (lovableImagesResponse.ok) {
              const lovableImagesList = await lovableImagesResponse.json();
              
              const lovableImages = lovableImagesList.map((fileName: string) => ({
                id: `lovable-${fileName}`,
                src: `/lovable-uploads/${fileName}`,
                alt: fileName,
                category: 'lovable',
                publicUrl: `/lovable-uploads/${fileName}`,
                tags: ['lovable', fileName.split('.')[0]]
              }));
              
              galleryImages = [...galleryImages, ...lovableImages];
            }
          } catch (err) {
            console.warn('Could not fetch Lovable uploads:', err);
          }
        }
      } else {
        // Legacy logic if needed
        const { data, error } = await supabase.storage
          .from('user_images')
          .list('', {
            limit: 100,
            sortBy: { column: 'created_at', order: 'desc' }
          });
        
        if (error) {
          throw error;
        }
        
        if (data) {
          // Filter out screenshots
          const filteredData = data.filter(file => 
            !isScreenshot(file.name.toLowerCase())
          );
          
          galleryImages = filteredData.map((file) => {
            const { data } = supabase.storage
              .from('user_images')
              .getPublicUrl(file.name);
              
            return {
              id: file.id,
              src: data.publicUrl,
              alt: file.name,
              category: 'user_images',
              publicUrl: data.publicUrl,
              tags: ['käyttäjän kuva', file.name.split('.')[0]]
            };
          });
        }
      }
      
      console.log(`Fetched ${galleryImages.length} images for gallery grid`);
      setImages(galleryImages);
    } catch (err: any) {
      console.error('Error fetching gallery images:', err);
      setError(err.message);
      toast.error('Virhe kuvien lataamisessa');
    } finally {
      setLoading(false);
    }
  };

  // Decide which click handler to use
  const handleImageClick = (image: GalleryImage) => {
    if (onSelect) {
      onSelect(image);
    } else if (onImageSelect) {
      onImageSelect(image);
    }
  };

  // Render the component
  if (isLoading || loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <p className="ml-3 text-gray-500">Ladataan kuvia...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 rounded-lg text-center">
        <p className="text-red-500 mb-2">Virhe kuvien lataamisessa:</p>
        <p className="text-sm text-gray-700">{error}</p>
        <Button 
          variant="outline" 
          size="sm" 
          className="mt-3"
          onClick={() => category && fetchImages(category)}
        >
          Yritä uudelleen
        </Button>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg text-center">
        <p className="text-gray-500">Ei kuvia saatavilla tässä kategoriassa.</p>
        <p className="text-sm text-gray-400 mt-2">Lataa ensin kuvia tähän kategoriaan.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {images.map((image) => (
        <div 
          key={image.id}
          className="aspect-square overflow-hidden rounded-md border border-gray-200 relative group cursor-pointer"
          onClick={() => handleImageClick(image)}
        >
          <img 
            src={image.src} 
            alt={image.alt}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
            onError={(e) => {
              console.error(`Failed to load image: ${image.src}`);
              (e.target as HTMLImageElement).src = "https://via.placeholder.com/300x300?text=Virhe+kuvan+lataamisessa";
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
            {(onSelect || onImageSelect) && (
              <div className="opacity-0 group-hover:opacity-100 transition-all bg-blue-600 text-white px-3 py-1 rounded-full text-xs">
                Valitse kuva
              </div>
            )}
            
            {/* Add special indicator for Lovable uploads */}
            {isLovableUpload(image.src) && (
              <div className="absolute top-2 left-2 bg-purple-600 text-white px-2 py-0.5 rounded text-xs opacity-80">
                Lovable
              </div>
            )}
            
            {/* Show category tag */}
            <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-0.5 rounded text-xs opacity-0 group-hover:opacity-80">
              {image.category}
            </div>
            
            {/* Add copy URL button if onCopyUrl function is provided */}
            {onCopyUrl && (
              <Button
                variant="outline"
                size="sm"
                className="absolute bottom-2 right-2 bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  onCopyUrl(image);
                }}
              >
                Kopioi URL
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default GalleryImageGrid;
